import type { ReactNode } from 'react'
import { Card } from './Surface'

export function StatTile({
  label,
  value,
  icon,
  tint = 'var(--accent)',
  delta,
  /** Dims the whole tile. For a number that is history rather than an achievement
   *  — an ended streak, most importantly, which must not be celebrated. */
  past,
}: {
  label: string
  value: string
  icon?: ReactNode
  tint?: string
  delta?: { value: number; suffix?: string }
  past?: boolean
}) {
  return (
    <Card className="mx-stat">
      {icon && (
        <div
          className="mx-stat-icon"
          style={
            past
              ? { background: 'var(--hairline)', color: 'var(--faint)' }
              : { background: `color-mix(in srgb, ${tint} 16%, transparent)`, color: tint }
          }
          aria-hidden="true"
        >
          {icon}
        </div>
      )}
      <div style={{ minWidth: 0 }}>
        <p className="mx-stat-value" style={past ? { color: 'var(--muted)' } : undefined}>
          {value}
          {delta && (
            <span
              style={{
                marginLeft: 8,
                fontSize: 'var(--text-xs)',
                fontFamily: 'var(--font-body)',
                /* A metric moving up is not a mark awarded. The ▲/▼ glyph
                   already carries the direction, so this only needs to be
                   legible, not celebratory. */
                color: delta.value >= 0 ? 'var(--info)' : 'var(--muted)',
              }}
            >
              {delta.value >= 0 ? '▲' : '▼'} {Math.abs(delta.value)}
              {delta.suffix ?? ''}
            </span>
          )}
        </p>
        <p className="mx-label" style={{ margin: '6px 0 0' }}>
          {label}
        </p>
      </div>
    </Card>
  )
}

/** Always labelled with what it is. A bare bar tells a student nothing, and this
 *  one can legitimately go down without them having done anything wrong. */
export function Bar({
  value,
  label,
  tint,
}: {
  /** 0–1. */
  value: number
  label?: string
  tint?: string
}) {
  const pct = Math.max(0, Math.min(1, value))
  const colour = tint ?? masteryColour(pct)

  return (
    <div>
      {label && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span className="mx-label">{label}</span>
          <span className="mx-label mx-numeric" style={{ color: 'var(--text)' }}>
            {Math.round(pct * 100)}%
          </span>
        </div>
      )}
      <div
        className="mx-bar"
        role="progressbar"
        aria-valuenow={Math.round(pct * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
      >
        <i style={{ width: `${pct * 100}%`, background: colour }} />
      </div>
    </div>
  )
}

/**
 * The colour of a proficiency, in three bands.
 *
 * Bands rather than a gradient because the three mean different things to a
 * student — solid, shaky, needs work — and a continuous ramp makes 49% and 51%
 * look like the same state when they are the ones worth telling apart.
 */
export function masteryColour(value: number): string {
  if (value >= 0.7) return 'var(--awarded)'
  if (value >= 0.4) return 'var(--warn)'
  return 'var(--withheld)'
}

export function Pill({
  children,
  tone = 'neutral',
  colour,
}: {
  children: ReactNode
  tone?: 'neutral' | 'accent' | 'awarded' | 'withheld' | 'warn'
  colour?: string
}) {
  const tones: Record<string, { bg: string; fg: string }> = {
    neutral: { bg: 'var(--hairline)', fg: 'var(--muted)' },
    accent: { bg: 'var(--accent-wash)', fg: 'var(--accent)' },
    awarded: { bg: 'var(--awarded-wash)', fg: 'var(--awarded)' },
    withheld: { bg: 'var(--withheld-wash)', fg: 'var(--withheld)' },
    warn: { bg: 'var(--warn-wash)', fg: 'var(--warn)' },
  }
  const style = colour
    ? { background: `color-mix(in srgb, ${colour} 16%, transparent)`, color: colour }
    : { background: tones[tone]!.bg, color: tones[tone]!.fg }

  return (
    <span className="mx-pill" style={style}>
      {children}
    </span>
  )
}

/** In the shape of what is coming, so the layout does not jump when it arrives.
 *  Only where the wait is real — a shimmer over a cached screen that will paint
 *  immediately just makes an instant load look slow. */
export function Skeleton({ width = '100%', height = 12 }: { width?: string | number; height?: number }) {
  return <div className="mx-skeleton" style={{ width, height }} aria-hidden="true" />
}

export function SkeletonCard({ lines = 3 }: { lines?: number }) {
  return (
    <Card>
      <Skeleton width="35%" />
      <div style={{ height: 12 }} />
      <Skeleton width="65%" height={20} />
      <div style={{ height: 14 }} />
      {Array.from({ length: lines }, (_, i) => (
        <div key={i} style={{ marginTop: i === 0 ? 0 : 8 }}>
          <Skeleton width={i === lines - 1 ? '70%' : '92%'} />
        </div>
      ))}
    </Card>
  )
}
